import axios from "axios";
import moment from "moment";
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ReactMarkdown from 'react-markdown';
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Popup from "reactjs-popup";
import 'reactjs-popup/dist/index.css';
import stylelist from "stylelist";
import { copy_slot, delete_slot, move_slot } from "../../actions/Slots";
import { disable_sync, enable_sync } from "../../actions/Sync";
import SyncConstants from "../../constants/SyncConstants";
import useCalendar from "../../hooks/useCalendar";
import useSlots from "../../hooks/useSlots";
import Gravatar from "../../libs/Gravatar";
import { getSlotMetaFromId } from "../../libs/SlotUtils";
import MinutesToTime from "../Expenses/MinutesToTime";
import AddSlot from "./AddSlot";
import ExpenseMask from "./ExpenseMask";
import useAuthentication from "../../hooks/useAuthentication";
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

const SlotTitle = (props) => {
    const Slots = useSlots();
    const Team = useSelector((state) => state.Team);
    const UserSettings = useSelector((state) => state.User);
    const SyncInfo = useSelector((state) => state.Sync);
    const dispatch = useDispatch();
    const popupRef = useRef();
    const popupRef2 = useRef();
    const Calendar = useCalendar();
    const Auth = useAuthentication();

    // Drag and Drop hooks
    const { attributes: draggableAttributes, listeners, setNodeRef: setDraggableRef, transform, isDragging } = useDraggable({
        id: `draggable-${props.slotId}`,
        data: {
            slotId: props.slotId,
            hasSlot: Slots.exists(props.slotId)
        },
        disabled: !Slots.exists(props.slotId) || props.controlMode
    });

    const { setNodeRef: setDroppableRef, isOver } = useDroppable({
        id: `droppable-${props.slotId}`,
        data: {
            slotId: props.slotId
        },
        disabled: false // Always accept drops, even on itself
    });

    // Combine droppable ref with draggable positioning
    const combinedRef = useCallback((node) => {
        setDroppableRef(node);
    }, [setDroppableRef]);

    const draggableStyle = {
        transform: CSS.Translate.toString(transform),
        cursor: Slots.exists(props.slotId) && !props.controlMode ? 'grab' : 'default',
    };

    const wrapperStyle = {
        opacity: isDragging ? 0.3 : 1,
    };

    // DOM-reference to some divs, used for the "Show more"-button
    const descriptionElementRef = useRef(null);
    const descriptionElementParentRef = useRef(null);

    // showMoreButton: display only if neccesarry (=description is high enough)
    const [showShowMoreButton, setShowShowMoreButton] = useState(false);
    const [showFullDescription, setShowFullDescriptiopn] = useState(false);

    const [showDetails, setShowDetails] = useState(false);

    // can be "disabled", "loading" or "enabled"
    const [slideSlot, setSlideSlot] = useState("disabled");
    const [targetSlideSlotName, setTargetSlideSlotName] = useState("");

    const [icalString, setIcalString] = useState("");

    const [expenseEntryData, setExpenseEntryData] = useState(false);

    const [showEditExpenseEntry, setShowEditExpenseEntry] = useState(false);

    const [showExpenseMask, setShowExpenseMask] = useState(false);

    // Context menu state
    const [contextMenu, setContextMenu] = useState(null);



    const loadExpenseEntries = useCallback(() => {
        axios({
            method: "get",
            auth: Auth.getAuthObject(),
            url: SyncConstants["syncBaseURL"] + "expenseEntries/bySlotId",
            params: {
                slotId: props.slotId
            },
            responseType: 'json'
        }).then((response) => {

            setExpenseEntryData(response.data);
            setShowExpenseMask(false);
        }).catch(() => { });
    }, [props.slotId, Auth])

    // Note: Removed automatic loading of expenses on render
    // Expenses are now loaded only when the popup is opened (onOpen handler)

    useEffect(() => {
        if (descriptionElementRef.current != null && descriptionElementParentRef.current != null) {
            if (descriptionElementRef.current.clientHeight > descriptionElementParentRef.current.clientHeight) {
                setShowShowMoreButton(true);
            }

        }
    }, [Slots.getBySlotId(props.slotId)]);

    const deleteSlotClicked = useCallback(() => {
        dispatch(delete_slot(props.slotId));
        if (props.deleteMergedSlot) {
            dispatch(delete_slot(props.mergedSlotId));
        }
    }, [props.slotId, props.deleteMergedSlot, props.mergedSlotId, dispatch])

    const pasteSlotFromKeyboard = useCallback((mode = "copy") => {
        if (props.clipboardMode == "copy") {
            dispatch(copy_slot(props.clipboardSlot, props.slotId));
        }

        if (props.clipboardMode == "cut") {
            dispatch(move_slot(props.clipboardSlot, props.slotId));
            props.setClipboard(false);
        }
    }, [props.clipboardMode, props.clipboardSlot, props.slotId, props.setClipboard, dispatch])

    const getSlotDateObject = () => {
        return moment(getSlotMetaFromId(props.slotId).slotDate);
    }

    useEffect(() => {
        if (Slots.exists(props.slotId)) {
            setIcalString(Calendar.createIcal(Slots.getBySlotId(props.slotId).content.slotTitle,
                getSlotDateObject(),
                getSlotMetaFromId(props.slotId).slotNumber,
                Team[getSlotMetaFromId(props.slotId).slotUser].email

            ));
        }
    }, [Slots.getBySlotId(props.slotId)])

    // Handle context menu
    const handleContextMenu = useCallback((e) => {
        if (!Slots.exists(props.slotId)) return;
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({
            x: e.clientX,
            y: e.clientY
        });
    }, [props.slotId, Slots]);

    // Close context menu on click outside
    useEffect(() => {
        const handleClick = () => setContextMenu(null);
        if (contextMenu) {
            document.addEventListener('click', handleClick);
            return () => document.removeEventListener('click', handleClick);
        }
    }, [contextMenu]);

    return <>

        {/* best case: we have a slot and whot its title with a detailed popup */}
        {(Slots.exists(props.slotId)) &&
            <>
                <Popup
                    on={["click"]}
                    mouseEnterDelay="650"
                    modal
                    onOpen={() => {
                        dispatch(disable_sync());
                        // Load expenses only when popup is opened
                        loadExpenseEntries();
                    }}
                    onClose={() => {
                        dispatch(enable_sync());
                        setShowExpenseMask(false);
                    }}
                    ref={popupRef2}
                    className={stylelist([showFullDescription || showExpenseMask || showEditExpenseEntry !== false, "scroll-y-overflow"])}
                    disabled={props.controlMode}
                    trigger={
                        <div
                            className={stylelist(
                                "slot-wrapper-droppable",
                                [isOver, "slot-drop-target"]
                            )}
                        >
                            <div
                                ref={combinedRef}
                                style={wrapperStyle}
                                className="slot-drop-zone w-100 h-100"
                            >
                                <div
                                    ref={setDraggableRef}
                                    style={draggableStyle}
                                    {...listeners}
                                    {...draggableAttributes}
                                    onContextMenu={handleContextMenu}
                                    className={stylelist(
                                    "slot-container cursor-pointer p-1 slot-draggable",
                                    "slot-" + Slots.getBySlotId(props.slotId).content.slotType,
                                    [Slots.getBySlotId(props.slotId).content.hasExpenses, "has-expenses"],
                                    [props.filterWord != "" && Slots.getBySlotId(props.slotId).content.slotTitle.toLowerCase().indexOf(props.filterWord.toLowerCase()) > -1, "filterActive"],
                                    [props.filterWord != "" && Slots.getBySlotId(props.slotId).content.slotTitle.toLowerCase().indexOf(props.filterWord.toLowerCase()) < 0, "filterInactive"],
                                    [Slots.getBySlotId(props.slotId).content.slotExpensesBooked, "expenses-booked"],
                                    [isDragging, "slot-dragging"]
                                )}
                                >

                            <small className="d-flex align-items-center justify-content-center">

                                {((props.controlMode && props.clipboardMode != "none") || (!props.controlMode)) &&

                                    <>
                                        {(props.stealthMode && (Slots.getBySlotId(props.slotId).content.slotTitle.toLowerCase().indexOf(props.filterWord.toLowerCase()) < 0 || props.filterWord.length == 0)) &&
                                            <>
                                                gebucht
                                            </>
                                        }

                                        {((!props.stealthMode) || (props.stealthMode && Slots.getBySlotId(props.slotId).content.slotTitle.toLowerCase().indexOf(props.filterWord.toLowerCase()) > -1 && props.filterWord.length > 0)) &&
                                            <>
                                                {Slots.getBySlotId(props.slotId).content.slotTitle}
                                            </>
                                        }
                                    </>
                                }

                                {(props.controlMode && props.clipboardMode == "none") &&
                                    <>

                                        <div className="d-flex justify-content-between">
                                            <div className="control-mode-icon" onClick={() => { props.setClipboard(props.slotId, "copy"); }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-files" viewBox="0 0 16 16">
                                                    <path d="M13 0H6a2 2 0 0 0-2 2 2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2 2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zm0 13V4a2 2 0 0 0-2-2H5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1zM3 4a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4z" />
                                                </svg>
                                            </div>

                                            <div className="control-mode-icon" onClick={() => { props.setClipboard(props.slotId, "cut"); }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-scissors" viewBox="0 0 16 16">
                                                    <path d="M3.5 3.5c-.614-.884-.074-1.962.858-2.5L8 7.226 11.642 1c.932.538 1.472 1.616.858 2.5L8.81 8.61l1.556 2.661a2.5 2.5 0 1 1-.794.637L8 9.73l-1.572 2.177a2.5 2.5 0 1 1-.794-.637L7.19 8.61 3.5 3.5zm2.5 10a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0zm7 0a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0z" />
                                                </svg>
                                            </div>


                                            <div className="control-mode-icon" onClick={() => { deleteSlotClicked(); }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    fill="currentColor" className="bi bi-trash" viewBox="0 0 16 16">
                                                    <path
                                                        d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                                    <path fillRule="evenodd"
                                                        d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                                                </svg>
                                            </div>

                                        </div>
                                    </>
                                }

                            </small>
                                </div>
                            </div>
                        </div>
                    }
                    position={stylelist(
                        "center",
                        [moment(Slots.getBySlotId(props.slotId).meta.slotDate).weekday() < 2, "left"],
                        [moment(Slots.getBySlotId(props.slotId).meta.slotDate).weekday() == 2, "bottom"],
                        [moment(Slots.getBySlotId(props.slotId).meta.slotDate).weekday() > 2, "right"],
                    )}
                >
                    <div className="px-3 py-3">
                        <div className="row" >
                            <div className="col-12">

                                <div className="row pt-3 ps-2">
                                    <div className="col-12 text-center">
                                        <Gravatar size={120} className={stylelist("avatar", "d-inline-block", "mb-3")} default="mp" email={Team[Slots.getBySlotId(props.slotId).meta.slotUser].email} />

                                    </div>
                                    <div className="col-12 text-center">
                                        <h4 className="w-100 d-inline-block text-left mt-3">{Slots.getBySlotId(props.slotId).content.slotTitle}</h4>
                                        <h6 className="w-100 d-inline-block text-left small text-muted">{Team[Slots.getBySlotId(props.slotId).meta.slotUser].realName}</h6>
                                    </div>
                                </div>



                                {!showExpenseMask &&
                                    <>

                                        <ReactMarkdown>{Slots.getBySlotId(props.slotId).content.slotDescription}</ReactMarkdown>
                                        <div className="row mt-1">

                                            {!showExpenseMask &&
                                                <div className="row">
                                                    <div className="col-12 text-center">
                                                        <div className="mb-4">

                                                            <Link className="badge bg-secondary mt-1 me-1" to={"/slots/edit/" + props.slotId}>Bearbeiten</Link>
                                                            <button
                                                                onClick={() => { popupRef2.current.close(); props.setClipboard(props.slotId, "copy"); }}
                                                                className="badge bg-secondary mt-1 me-1">Kopieren</button>

                                                            <button
                                                                onClick={() => { popupRef2.current.close(); props.setClipboard(props.slotId, "cut"); }}
                                                                className="badge bg-secondary mt-1 me-1">Ausschneiden</button>

                                                            <button
                                                                onClick={() => { setShowExpenseMask(true); }}
                                                                className="badge bg-secondary mt-1 me-1">Aufwände einbuchen</button>


                                                            <button onClick={() => { popupRef2.current.close(); deleteSlotClicked(); }} className="badge bg-secondary mt-1 me-1">Löschen</button>


                                                        </div>
                                                    </div>
                                                </div>
                                            }

                                            <div className="col-12 mt-2">
                                                <ul className="bullet">
                                                    {Slots.getBySlotId(props.slotId).content.slotType == "simple" &&
                                                        <>
                                                            <li className="small"><b>Einfacher Slot.</b> Einfache Slots besitzen keine Verknüpfung zu GLaDOS, Trello oder anderen externen Systemen.</li>
                                                        </>
                                                    }

                                                    {Slots.getBySlotId(props.slotId).content.slotType == "school" &&
                                                        <>
                                                            <li className="small"><b>Berufsschule.</b> Möglicherweise ist der:die Mitarbeiter:in nach der Berufsschule noch im Unternehmen anzutreffen.</li>
                                                        </>
                                                    }

                                                    <li className="small"><b>iCal.</b> Diesen Termin in deinen Kalender <a href={icalString}>herunterladen</a>.</li>

                                                    {(expenseEntryData === false) &&
                                                        <><li className="small"><b>Gebuchte Aufwände.</b> Wir synchronisieren gerade alle Aufwände (sofern vorhanden).</li></>
                                                    }


                                                    {expenseEntryData.length == 0 &&
                                                        <><li className="small"><b>Keine Aufwände gebucht.</b> Aktuell sind keine Aufwände gebucht.</li></>
                                                    }

                                                </ul>


                                                {expenseEntryData.length > 0 &&
                                                    <>


                                                        {showEditExpenseEntry != false &&
                                                            <>
                                                                <div className="container bg-light p-3 border">
                                                                    <div className="row">
                                                                        <div className="col-12">
                                                                            <ExpenseMask update={true} updateEntry={showEditExpenseEntry} afterSubmit={(needDeepUpdate) => {
                                                                                setShowEditExpenseEntry(false);
                                                                                loadExpenseEntries();
                                                                            }} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        }

                                                        {!showEditExpenseEntry &&
                                                            <>  <h6 className="mt-4"><b>Aufwände</b></h6>
                                                                <ul className="bullet">
                                                                    {expenseEntryData.map(e => {
                                                                        return <React.Fragment key={e.id}><li className="small">{e.comment} (<MinutesToTime minutes={e.minutes} />, <span className="text-primary cursor-pointer" onClick={() => { setShowEditExpenseEntry(e) }}>Bearbeiten</span>)</li></React.Fragment>
                                                                    })}
                                                                </ul>
                                                            </>
                                                        }


                                                    </>
                                                }


                                            </div>
                                        </div>
                                    </>}
                            </div>
                        </div>

                        {showExpenseMask &&
                            <div className="row mt-3" onClick={(e) => e.stopPropagation()}>
                                <div className="col-12">
                                    <>
                                        <ExpenseMask
                                            slotId={props.slotId}
                                            customerId={Slots.getBySlotId(props.slotId)?.content?.slotCustomerId}
                                            afterSubmit={loadExpenseEntries}
                                        />
                                        <button
                                            onClick={() => { setShowExpenseMask(false); }}
                                            className="badge bg-secondary mt-1 me-1">Abbrechen</button>
                                    </>
                                </div>
                            </div>
                        }
                    </div>
                </Popup>

                {/* Modern Context Menu */}
                {contextMenu && (
                    <div
                        className="modern-context-menu"
                        style={{
                            position: 'fixed',
                            top: contextMenu.y,
                            left: contextMenu.x,
                            zIndex: 10000
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="context-menu-item" onClick={() => {
                            popupRef2.current.open();
                            setContextMenu(null);
                        }}>
                            <i className="bi bi-eye"></i> Details anzeigen
                        </div>
                        <div className="context-menu-divider"></div>
                        <div className="context-menu-item" onClick={() => {
                            props.setClipboard(props.slotId, "copy");
                            setContextMenu(null);
                        }}>
                            <i className="bi bi-files"></i> Kopieren
                        </div>
                        <div className="context-menu-item" onClick={() => {
                            props.setClipboard(props.slotId, "cut");
                            setContextMenu(null);
                        }}>
                            <i className="bi bi-scissors"></i> Ausschneiden
                        </div>
                        <div className="context-menu-divider"></div>
                        <div className="context-menu-item context-menu-danger" onClick={() => {
                            deleteSlotClicked();
                            setContextMenu(null);
                        }}>
                            <i className="bi bi-trash"></i> Löschen
                        </div>
                    </div>
                )}
            </>
        }

        {/* also a good case: we have no slot and no other slot in the clipboard - suggest to add a new one */}
        {(!Slots.exists(props.slotId) && !props.clipboardSlot) &&
            <div
                ref={setDroppableRef}
                className={stylelist(
                    "slot-empty-container w-100 h-100 d-flex align-items-center justify-content-center",
                    [isOver, "slot-drop-target-empty"]
                )}
                style={{ minHeight: '80px' }}
            >
                {/* Show skeleton loading animation while syncing */}
                {SyncInfo.syncStatus === "working" && (
                    <div className="slot-container slot-skeleton w-100">
                        <div className="skeleton-box"></div>
                    </div>
                )}

                {/* Show plus icon when not syncing */}
                {SyncInfo.syncStatus !== "working" && (
                    <Popup modal ref={popupRef} trigger={
                        <div className="cursor-pointer hover text-muted py-5 w-100 text-center">
                            <span className="icon-plus"></span>
                        </div>
                    } position="bottom center">
                        <div>
                            <AddSlot closeHandler={() => { popupRef.current.close() }} slotId={props.slotId} />
                        </div>
                    </Popup>
                )}
            </div>
        }

        {/* not bad: we have no slot but another slot in our clipboard. offer to paste it! */}
        {(props.clipboardSlot && !Slots.exists(props.slotId)) &&
            <div
                ref={setDroppableRef}
                onClick={pasteSlotFromKeyboard}
                className={stylelist(
                    "cursor-pointer text-muted hover h-100 py-5 w-100 slot-empty-container",
                    [isOver, "slot-drop-target-empty"]
                )}
                style={{ minHeight: '80px' }}
            >
                <h6><span className="icon-circle-down"></span></h6>
            </div>
        }

    </>
}

export default SlotTitle